import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Bell, BellOff, Plus, LucideIconData } from 'lucide-angular';
import { Product, ProductVariant, VARIANT_LABELS } from '../../../../shared/models/product.model';
import {
  formatOriginalPrice,
  formatPrice,
  isSingleProduct,
} from '../../../../shared/utils/product.utils';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { VariantBadgeComponent } from '../../../../shared/ui/variant-badge/variant-badge.component';
import { StockAlertStore } from '../../../../shared/services/stock-alert.store';
import { highlightMatch, HighlightSegment } from '../../data/search.utils';

const FALLBACK_IMAGE_URL = '/images/hero/broken_hero.jpg';

@Component({
  selector: 'app-search-result-row',
  imports: [IconComponent, VariantBadgeComponent],
  templateUrl: './search-result-row.component.html',
  styleUrl: './search-result-row.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'option',
    '[attr.id]': 'rowId()',
    '[attr.aria-selected]': 'highlighted()',
    '[class.is-highlighted]': 'highlighted()',
  },
})
export class SearchResultRowComponent {
  private readonly stockAlertStore = inject(StockAlertStore);

  product = input.required<Product>();
  query = input<string>('');
  highlighted = input<boolean>(false);
  rowId = input.required<string>();

  select = output<Product>();
  quickAdd = output<Product>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();

  protected readonly plusIcon: LucideIconData = Plus;
  protected readonly bellIcon: LucideIconData = Bell;
  protected readonly bellOffIcon: LucideIconData = BellOff;

  protected readonly nameSegments = computed<HighlightSegment[]>(() =>
    highlightMatch(this.product().name, this.query()),
  );

  protected readonly priceLabel = computed(() => formatPrice(this.product()));

  protected readonly originalPriceLabel = computed(() =>
    formatOriginalPrice(this.product()),
  );

  protected readonly variant = computed<ProductVariant>(
    () => this.product().variant ?? 'non-foil',
  );

  protected readonly variantLabel = computed(() => VARIANT_LABELS[this.variant()]);

  protected readonly isSingle = computed(() => isSingleProduct(this.product()));

  protected readonly rarity = computed(() => this.product().rarity?.trim() || null);

  protected readonly canQuickAdd = computed(() => {
    const p = this.product();
    return (p.stock ?? 0) > 0 && !p.prepurchase;
  });

  protected readonly isOutOfStock = computed(() => {
    const p = this.product();
    if (p.prepurchase) return (p.prepurchase_stock ?? 0) === 0;
    return (p.stock ?? 0) === 0;
  });

  /** Producto agotado regular (no precompra) → habilitamos el CTA "Avisarme". */
  protected readonly canNotify = computed(() => {
    const p = this.product();
    return (p.stock ?? 0) === 0 && !p.prepurchase;
  });

  protected readonly isNotifyActive = computed(() => {
    const id = this.product().id;
    return id != null && this.stockAlertStore.getByProductId(Number(id)) !== null;
  });

  protected readonly imageUrl = computed(() => this.product().imageUrl || FALLBACK_IMAGE_URL);

  protected onSelect(event: MouseEvent): void {
    // Mousedown handler del overlay ya evita cerrar el panel.
    event.preventDefault();
    this.select.emit(this.product());
  }

  protected onQuickAdd(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.quickAdd.emit(this.product());
  }

  protected onNotifyClick(event: MouseEvent): void {
    this.activateNotify(event);
  }

  protected onNotifyKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.activateNotify(event);
  }

  private activateNotify(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.isNotifyActive()) {
      this.notifyCancel.emit(this.product());
    } else {
      this.notifyRequest.emit(this.product());
    }
  }

  protected onImgError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && !img.src.endsWith(FALLBACK_IMAGE_URL)) {
      img.src = FALLBACK_IMAGE_URL;
    }
  }
}
